import { BoltIcon, BoltSlashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import {
  type IAppraisalReviewFieldsConfig,
  appraisalReviewFieldGroups,
  AppraisalReviewFieldSection,
  AppraisalReviewFieldSectionText,
  appraisalReviewOptionalFieldKeys,
  defaultInputs,
} from 'pages/Loan/Underwriting/ReviewForm/constants'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

/**
 * Admin UI (Conditions & Templates > Valuation Review Templates > Appraisal Review Fields).
 * Mirrors Underwriting Summary config: eye = visible, bolt = required.
 * Optional fields default hidden so the Valuation Review screen stays unchanged until enabled.
 */
export const AppraisalReviewFieldsConfig = () => {
  const fieldInputs = defaultInputs()
  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [data, setData] = useState<IAppraisalReviewFieldsConfig>({})

  useEffect(() => {
    setLoading(true)
    getAdminConfig('appraisalReviewFieldsConfig')
      .then((res) => {
        if (res && typeof res === 'object') setData(res)
      })
      .finally(() => setLoading(false))
  }, [])

  const isOptional = (key: string) => (appraisalReviewOptionalFieldKeys as readonly string[]).includes(key)

  const onToggle = (key: string, prop: 'visible' | 'required', value: boolean) => {
    const next = cloneDeep(data)
    if (!next[key]) next[key] = {}
    next[key][prop] = value
    setData(next)
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    await setAdminConfig('appraisalReviewFieldsConfig', data)
    setLoading(false)
    setChanged(false)
  }

  return (
    <div>
      <LayoutLoading show={isLoading} />
      <p className="text-sm text-gray-500 mb-4">
        Control which fields appear on the Valuation Review screen and whether they are required. New fields start
        hidden so existing loans are unchanged until you enable them.
      </p>

      <div className="grid grid-cols-3 mt-4 gap-2 mb-4">
        {Object.values(AppraisalReviewFieldSection).map((section, index) => (
          <div key={section} className="contents">
            <div className="col-span-full">
              <RenderInput
                Key={`section-${section}`}
                input={{
                  inputType: 'section',
                  title: `${index + 1}. ${AppraisalReviewFieldSectionText[section]}`,
                }}
                onChange={() => {}}
              />
            </div>
            {appraisalReviewFieldGroups[section].map((key, subIndex) => {
              const input = fieldInputs[key]
              if (!input) return null
              const cfg = data[key] || {}
              const visible = cfg.visible !== undefined ? cfg.visible !== false : !isOptional(key)
              const required = cfg.required !== undefined ? !!cfg.required : !!input.required
              const Icon = visible ? EyeIcon : EyeSlashIcon
              const RequiredIcon = required ? BoltIcon : BoltSlashIcon

              return (
                <div className="flex justify-between items-center" key={key}>
                  <p className={`text-sm ${visible ? '' : 'line-through opacity-60'}`}>
                    {subIndex + 1}. {input.title}
                    {isOptional(key) && <span className="ml-1 text-xs text-gray-400">(optional)</span>}
                  </p>
                  <div className="flex gap-2 items-center">
                    <span className={`btn-icon ${visible ? '' : 'opacity-60'}`} onClick={() => onToggle(key, 'visible', !visible)}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span
                      className={`btn-icon ${required ? '' : 'opacity-60'}`}
                      onClick={() => onToggle(key, 'required', !required)}
                    >
                      <RequiredIcon className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      <div className="flex flex-row justify-center gap-4">
        <Button onClick={onSubmit} loading={isLoading} disabled={!isChanged}>
          Save
        </Button>
      </div>

      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
